import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {Button, CalendarPicker, NavBar, Popup, SearchBar, Skeleton} from "antd-mobile";
import {DownOutline, SearchOutline} from 'antd-mobile-icons';
import {getNavBarContainerStyle} from "../../../utils/deviceUtils";
import {isAndroid} from "../../../utils/deviceUtils";
import {useQuery} from '@apollo/client';
import DeliveredCard from "./components/DeliveredCard";
import EmptyState from "../../../components/EmptyState";
import {getTimestampsFromDate, isSameDay, timestampToDate} from "utils/dateTimeUtils";
import {DeliveryTaskOrderListResponse, GET_DELIVERY_TASK_ORDER_LIST} from "../graphql/queries/deliveryTaskOrderList";
import {t} from "i18n";
import {formatLocalizedDate} from "utils/dateFormatUtils";
import {DeliveredParcelIcon} from "assets/icons";


interface DeliveredProps {
}

const DeliveredPage: React.FC<DeliveredProps> = () => {
    const navigate = useNavigate();
    const [showCalendar, setShowCalendar] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [searchValue, setSearchValue] = useState<string>('');
    const [selectedDate, setSelectedDate] = useState<Date | null>(new Date()); // 默认选中当天

    // 从selectedDate计算时间戳传递给后端接口
    const { startTimestamp, endTimestamp } = selectedDate ? 
        getTimestampsFromDate(selectedDate) : 
        getTimestampsFromDate(new Date());

    // 使用 useQuery 直接调用后端接口
    const { data, loading, refetch } = useQuery<DeliveryTaskOrderListResponse>(GET_DELIVERY_TASK_ORDER_LIST, {
        variables: {
            maxDeliveryTaskId: 0, // 设置为0以获取所有数据
            startTimestamp: startTimestamp,
            endTimestamp: endTimestamp,
        },
        fetchPolicy: 'cache-and-network', // 有网络时获取最新数据，无网络时使用缓存
        errorPolicy: 'all', // 即使有错误也返回缓存数据
    });

    const deliveryTaskOrderList = data?.getDeliveryTaskOrderList?.deliveryTaskOrderList || [];

    // 当时间戳变化时重新获取数据
    useEffect(() => {
        refetch({
            maxDeliveryTaskId: 0,
            startTimestamp: startTimestamp,
            endTimestamp: endTimestamp,
        });
    }, [startTimestamp, endTimestamp, refetch]);

    const allOrders = useMemo(() => {
        return (deliveryTaskOrderList.filter(item => item?.isDelivered) || [])
            .map(item => ({
                id: item.id,
                trackingNumber: item.trackingNumber || '',
                deliveryAttempts: item.orderWithAddress?.order?.deliveryAttempts || 0,
                stopNumber: item.stopNumber,
                fullName: item.orderWithAddress?.deliveryAddress?.fullName || '',
                address: `${item.orderWithAddress?.deliveryAddress?.addressLine1 || ''} ${item.orderWithAddress?.deliveryAddress?.addressLine2 || ''}`.trim(),
                routeNumber: item.routeName,
                deliveredAt: item.orderWithAddress?.order?.deliveredAt || '',
                expectedDeliveryTime: item.orderWithAddress?.order?.expectedDeliveryTime || '',
                deliveryType: item?.orderWithAddress?.order?.overallStatus || '',
                phone: item.orderWithAddress?.deliveryAddress?.phoneNumber || '',
            }));
    }, [deliveryTaskOrderList]);

    const filteredOrders = useMemo(() => {
        return allOrders
            .filter(order => (searchValue === '') || order.trackingNumber.includes(searchValue))
            .filter(order => (selectedDate === null) || isSameDay(timestampToDate(order.deliveredAt), selectedDate))
            .sort((a, b) => {
                if (!a.deliveredAt) return 1;
                if (!b.deliveredAt) return -1;
                return timestampToDate(b.deliveredAt).getTime() - timestampToDate(a.deliveredAt).getTime();
            });
    }, [allOrders, searchValue, selectedDate]);

    const handleDateSelect = (date: Date | null) => {
        setSelectedDate(date);
    };

    const handleSearch = (value: string) => {
        setSearchValue(value);
    };

    const clearFilters = () => {
        setSearchValue('');
        setSelectedDate(new Date()); // 重置为当天
    };

    // 检查是否与当天不同来判断是否在过滤
    const isFiltering = searchValue !== '' || (selectedDate && !isSameDay(selectedDate, new Date()));

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            <div {...getNavBarContainerStyle()}>
                <NavBar
                onBack={() => navigate(-1)}
                right={
                    <div className="flex items-center justify-end">
                        {isFiltering && (
                            <Button color='primary' fill='none' onClick={clearFilters}>
                                {t(`pages.useMenu.clear`)}
                            </Button>
                        )}

                        <Button
                            color='primary'
                            fill='none'
                            onClick={() => setShowSearch(true)}
                        >
                            <SearchOutline/>
                        </Button>
                    </div>
                }>
                {t(`pages.useMenu.delivered`)}
                </NavBar>
            </div>

            {/* 搜索弹出框 */}
            <Popup
                visible={showSearch}
                onMaskClick={() => setShowSearch(false)}
                position='top'
                bodyStyle={{
                    borderBottomLeftRadius: '0.5em',
                    borderBottomRightRadius: '0.5em',
                    minHeight: '3.75em',
                    padding: '0.75em',
                    top: isAndroid() ? '24px' : '0px' // 为Android设备添加顶部偏移
                }}
            >
                <SearchBar
                    placeholder={t('pages.delivery.delivered.searchPlaceholder')}
                    value={searchValue}
                    onChange={setSearchValue}
                    onSearch={handleSearch}
                    onCancel={() => {
                        setShowSearch(false);
                    }}
                />
            </Popup>

            {/* 日期选择器 */}
            <div className="p-2 bg-white">
                <Button
                    block
                    fill='none'
                    onClick={() => setShowCalendar(true)}
                >
                    <div className="flex items-center justify-center w-full">
                        <span className="mr-1">
                            {selectedDate ? formatLocalizedDate(selectedDate) : t('pages.delivery.delivered.selectDate')}
                        </span>
                        <DownOutline/>
                    </div>
                </Button>
            </div>

            <CalendarPicker
                visible={showCalendar}
                onClose={() => setShowCalendar(false)}
                onConfirm={handleDateSelect}
                defaultValue={selectedDate}
                selectionMode="single"
                min={new Date(2025, 0, 1)} // 设置最小可选日期为2025年1月1日
                max={new Date()} // 设置最大可选日期为今天
                title={t('pages.delivery.delivered.selectDate')}
            />

            {/* 统计总数 - 仅在有订单时显示 */}
            {filteredOrders.length > 0 && (
                <div className="px-4 py-3 bg-white">
                    <div className="flex items-center">
                        <DeliveredParcelIcon width={20} height={20} className="mr-2" />
                        <span className="text-gray-700">{t('pages.delivery.delivered.totalDeliveredParcels')}: </span>
                        <span className="text-black font-bold ml-1">{filteredOrders.length}</span>
                    </div>
                </div>
            )}

            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex flex-col h-screen">
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                        <Skeleton.Title animated />
                        <Skeleton.Paragraph lineCount={4} animated />
                    </div>
                ):(
                    <>
                        {filteredOrders.length > 0 ? (
                            <div className="bg-gray-100">
                                {filteredOrders.map((order, index) => (
                                    <DeliveredCard key={index} order={order}/>
                                ))}
                            </div>
                        ) : (
                            <EmptyState/>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default DeliveredPage;
